/**
 * SoftphoneBar (Phase 4 / Chunk 2)
 *
 * Docked call bar shown on CRM pages whenever a call is active (connecting /
 * ringing / open): mute, hang up, and a live duration timer. Reads
 * SoftphoneContext; renders nothing when idle (except a brief error toast).
 * Fixed bottom-right, primary accent. Mounted once at the CRM module root so it
 * persists across CRM navigation.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneOff, PhoneIncoming, PhoneForwarded, Pause, Mic, MicOff, Loader2, X, UserPlus, Check, Headphones } from 'lucide-react';
import { useSoftphone } from '../contexts/SoftphoneContext';
import AudioDevicePicker from './AudioDevicePicker';
import { useCallWorkspace } from '../contexts/CallWorkspaceContext';
import { formatPhone, relTime } from '../lib';
import { useNow } from '../useNow';
import api from '../../services/api';
import type { CallsAnalytics } from '../types';
import { Z } from './ui';

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

interface PickTeammate { id: string; name: string; onCall: boolean; }

/* call-dock tier — kept in step with SoftphoneBar */
const DOCK_POS = `fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] right-4 ${Z.callDock} lg:bottom-4 `;

export interface SoftphoneBarProps {
  /**
   * `fixed` (default) is today's self-positioning dock — every card keeps the
   * exact class string it has always had, because this is the toggle-off path.
   * `stack` strips ONLY the positioning prefix so the D-2 overlay manager can
   * own the corner and measure the dock's height; the cards themselves (widths,
   * borders, contents, branches) are the same in both modes.
   */
  variant?: 'fixed' | 'stack';
}

export default function SoftphoneBar({ variant = 'fixed' }: SoftphoneBarProps = {}) {
  const { callStatus, active, muted, toggleMute, hangup, startedAt, error, clearError,
    incoming, acceptIncoming, rejectIncoming, waiting, acceptWaiting, declineWaiting,
    park, transferBlind, transition, reconnecting } = useSoftphone();
  const navigate = useNavigate();
  const { openWorkspace } = useCallWorkspace();
  // Live call duration, same 1s cadence off the ONE shared clock (D-9) — and,
  // as before, no timer at all unless a call is actually connected.
  const ticking = callStatus === 'open' && !!startedAt;
  const nowTs = useNow(1000, ticking);
  const elapsed = ticking && startedAt ? Math.max(0, Math.floor((nowTs - startedAt) / 1000)) : 0;

  /** Positioning prefix in `fixed` mode; nothing at all in `stack` mode. */
  const dockCls = (rest: string) => (variant === 'fixed' ? `${DOCK_POS}${rest}` : rest);

  // Clicking an incoming-call popup (or answering it) jumps to the matched lead
  // and opens the full-screen call workspace over the page — the recruiter gets
  // the script / notes / prescreen in front of them as they take the call. The
  // popup stays above the workspace (z-50 over z-40), so Accept is still one tap
  // away. A no-op for an unknown caller (no matched contact to open).
  const openWorkspaceForContact = (contactId?: string | null) => {
    if (!contactId) return;
    openWorkspace(contactId);
    navigate(`/crm/contacts/${contactId}`);
  };
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [online, setOnline] = useState<PickTeammate[]>([]);
  // Mic/speaker picker panel (same in-bar pattern as the transfer picker).
  const [audioOpen, setAudioOpen] = useState(false);
  // Unknown-caller "create a lead from this number" state. `createdLead` remembers
  // the number we just spun a lead from so the button flips to "open workspace"
  // instead of inviting a second create; it clears when the call ends.
  const [creatingLead, setCreatingLead] = useState(false);
  const [createLeadError, setCreateLeadError] = useState<string | null>(null);
  const [createdLead, setCreatedLead] = useState<{ number: string; contactId: string } | null>(null);

  // Turn the caller's number into a lead in one click, then drop into the call
  // workspace (notes / prescreen / script) for the live call. No welcome SMS —
  // the patient is already on the line. Creating with only a phone number is
  // allowed server-side; if that number already belongs to a lead the endpoint
  // links to it (matched) and we open that record instead of duplicating.
  const createLeadFromCall = async (number: string) => {
    if (!number || creatingLead) return;
    setCreatingLead(true);
    setCreateLeadError(null);
    try {
      const res = await api.post('/api/crm/contacts', { phone: number, send_welcome: false });
      const contact = res.data?.contact;
      if (!contact?.id) throw new Error('No contact returned');
      setCreatedLead({ number, contactId: contact.id });
      openWorkspace(contact.id);
      navigate(`/crm/contacts/${contact.id}`);
    } catch (e: any) {
      setCreateLeadError(e?.response?.data?.error || 'Could not create a lead from this number.');
    } finally {
      setCreatingLead(false);
    }
  };

  // Load online teammates when the transfer picker opens (on demand — not polled).
  useEffect(() => {
    if (!pickerOpen) return;
    let active2 = true;
    setPickerLoading(true);
    (async () => {
      try {
        const [teamRes, onlineRes, callsRes] = await Promise.all([
          api.get('/api/crm/team'),
          api.get('/api/crm/voice/online'),
          api.get('/api/crm/analytics/calls').catch(() => ({ data: null })), // best-effort: never block the picker
        ]);
        const onlineIds = new Set<string>(onlineRes.data?.online || []);
        const calls = (callsRes.data || null) as CallsAnalytics | null;
        const onCallIds = new Set<string>(
          (calls?.recruiters || [])
            .filter((r) => r.live?.on_call)
            .map((r) => r.user_id),
        );
        const team: any[] = teamRes.data?.team || [];
        if (active2) {
          setOnline(
            team
              .filter((t) => onlineIds.has(t.id))
              .map((t) => ({ id: t.id, name: t.name, onCall: onCallIds.has(t.id) })),
          );
        }
      } catch {
        if (active2) setOnline([]);
      } finally {
        if (active2) setPickerLoading(false);
      }
    })();
    return () => { active2 = false; };
  }, [pickerOpen]);

  // Clear the create-lead state once the call ends so the next unknown caller
  // starts fresh (and a stale "open workspace" contact id can't linger).
  useEffect(() => {
    if (callStatus === 'idle') { setCreatedLead(null); setCreateLeadError(null); }
  }, [callStatus]);

  // An inbound call is ringing and we're not already on a call → answer prompt.
  if (incoming && callStatus === 'idle') {
    const contactId = incoming.contactId;
    const clickable = Boolean(contactId);
    return (
      <div
        data-softphone
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        title={clickable ? 'Open the call workspace for this lead' : undefined}
        onClick={clickable ? () => openWorkspaceForContact(contactId) : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                  e.preventDefault();
                  openWorkspaceForContact(contactId);
                }
              }
            : undefined
        }
        className={dockCls(`w-72 rounded-xl border border-primary-300 bg-white p-4 text-left shadow-lg dark:border-primary-800 dark:bg-gray-800${
          clickable
            ? ' cursor-pointer transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700/60'
            : ''
        }`)}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
            <PhoneIncoming className="h-5 w-5 animate-pulse" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {incoming.name || 'Incoming call'}
            </p>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">{incoming.from ? formatPhone(incoming.from) : 'Unknown caller'}</p>
            {incoming.ownerName && (
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">Assigned to {incoming.ownerName}</p>
            )}
            {incoming.lastContactedByName && (
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                Last contact: {incoming.lastContactedByName}
                {incoming.lastContactedAt && ` · ${relTime(incoming.lastContactedAt)}`}
              </p>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); rejectIncoming(); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700"
          >
            <PhoneOff className="h-4 w-4" /> Decline
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); acceptIncoming(); openWorkspaceForContact(contactId); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-green-700"
          >
            <Phone className="h-4 w-4" /> Accept
          </button>
        </div>
      </div>
    );
  }

  // A call-waiting call is still ringing but the current call has already ended →
  // present it like a normal incoming prompt (acceptWaiting has no call to swap off).
  if (waiting && callStatus === 'idle') {
    const contactId = waiting.contactId;
    const clickable = Boolean(contactId);
    return (
      <div
        data-softphone
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        title={clickable ? 'Open the call workspace for this lead' : undefined}
        onClick={clickable ? () => openWorkspaceForContact(contactId) : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                  e.preventDefault();
                  openWorkspaceForContact(contactId);
                }
              }
            : undefined
        }
        className={dockCls(`w-72 rounded-xl border border-primary-300 bg-white p-4 text-left shadow-lg dark:border-primary-800 dark:bg-gray-800${
          clickable
            ? ' cursor-pointer transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700/60'
            : ''
        }`)}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
            <PhoneIncoming className="h-5 w-5 animate-pulse" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {waiting.name || 'Incoming call'}
            </p>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">{waiting.from ? formatPhone(waiting.from) : 'Unknown caller'}</p>
            {waiting.ownerName && (
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">Assigned to {waiting.ownerName}</p>
            )}
            {waiting.lastContactedByName && (
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                Last contact: {waiting.lastContactedByName}
                {waiting.lastContactedAt && ` · ${relTime(waiting.lastContactedAt)}`}
              </p>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); declineWaiting(); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700"
          >
            <PhoneOff className="h-4 w-4" /> Decline
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); acceptWaiting(); openWorkspaceForContact(contactId); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-green-700"
          >
            <Phone className="h-4 w-4" /> Accept
          </button>
        </div>
      </div>
    );
  }

  // Idle: show a brief error toast if a call just failed, else nothing.
  if (callStatus === 'idle') {
    if (!error) return null;
    return (
      <div data-softphone className={dockCls('max-w-xs rounded-xl border border-red-200 bg-white p-3 shadow-lg dark:border-red-800 dark:bg-gray-800')}>
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <button onClick={clearError} className="shrink-0 text-gray-400 hover:text-gray-600" title="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const label =
    transition === 'parking' ? 'Parking…' :
    transition === 'transferring' ? 'Transferring…' :
    reconnecting ? 'Reconnecting…' :
    callStatus === 'connecting' ? 'Calling…' :
    callStatus === 'ringing' ? 'Ringing…' :
    fmt(elapsed);

  const connected = callStatus === 'open';
  // Park/transfer only make sense on a connected call and not mid-handoff.
  const canHandoff = connected && !transition;

  // An unknown caller: an active call whose number matched no existing lead
  // (inbound screen-pop miss, or an outbound dial-pad call to a not-yet-a-lead
  // number). Offer to create a lead from the number. Guard on a real number so a
  // parked-call pickup ("Parked call") never shows it.
  const unknownCallerNumber =
    active && !active.contactId && (active.to || '').replace(/\D/g, '').length >= 10
      ? active.to
      : null;
  const leadAlreadyCreated = Boolean(createdLead && createdLead.number === unknownCallerNumber);

  // Who/what is on the line: the contact name when known (it differs from the
  // raw number on outbound; inbound has no name so the number is the title),
  // with the formatted number as a subline so the recruiter always sees it.
  const callNumber = active?.to ? formatPhone(active.to) : null;
  const callName = active?.name && active.name !== active?.to && active.name !== '(no name)' ? active.name : null;
  const callTitle = callName || callNumber || 'Call';

  return (
    <div data-softphone className={dockCls('w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800')}>
      {/* Call-waiting: a second caller is ringing while this call is live. */}
      {waiting && (
        <div className="mb-3 rounded-lg border border-primary-300 bg-primary-50 p-2 dark:border-primary-800 dark:bg-primary-900/20">
          <div className="flex items-center gap-2">
            <PhoneIncoming className="h-4 w-4 shrink-0 animate-pulse text-primary-600 dark:text-primary-400" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-gray-900 dark:text-gray-100">{waiting.name || 'Call waiting'}</p>
              <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">{waiting.from ? formatPhone(waiting.from) : 'Unknown caller'}</p>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-end gap-1.5">
            <button
              onClick={declineWaiting}
              className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-red-700"
            >
              <PhoneOff className="h-3.5 w-3.5" /> Decline
            </button>
            <button
              onClick={acceptWaiting}
              title="Answer — ends the current call"
              className="inline-flex items-center gap-1 rounded-md bg-green-600 px-2 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-green-700"
            >
              <Phone className="h-3.5 w-3.5" /> Answer
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-full ${
            connected
              ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
          }`}
        >
          <Phone className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {callTitle}
          </p>
          {callName && callNumber && (
            <p className="truncate text-xs text-gray-400 dark:text-gray-500">{callNumber}</p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {connected && !transition && !reconnecting ? (
              <span className="tabular-nums">{label}</span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                {label}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Unknown caller → create a lead from the number and jump into the
          workspace. Once created it flips to a re-open shortcut (the call bar
          stays above the workspace, so this is the way back if it's dismissed). */}
      {unknownCallerNumber && (
        <div className="mt-3">
          {leadAlreadyCreated ? (
            <button
              onClick={() => openWorkspaceForContact(createdLead!.contactId)}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 transition-colors duration-150 hover:bg-green-100 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300 dark:hover:bg-green-900/40"
            >
              <Check className="h-4 w-4" /> Lead created · Open workspace
            </button>
          ) : (
            <button
              onClick={() => createLeadFromCall(unknownCallerNumber)}
              disabled={creatingLead}
              title="Create a lead from this number and open the call workspace"
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary-300 bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300 dark:hover:bg-primary-900/40"
            >
              {creatingLead ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              {creatingLead ? 'Creating lead…' : 'Create lead & open workspace'}
            </button>
          )}
          {createLeadError && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{createLeadError}</p>
          )}
        </div>
      )}

      {/* Audio devices — pick the mic/speaker the call is actually using.
          Mid-call switches apply live (SoftphoneContext hot-swaps the track). */}
      {audioOpen && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900/40">
          <p className="px-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Audio devices</p>
          <AudioDevicePicker />
        </div>
      )}

      {/* Transfer picker — online teammates only (server enforces it too). */}
      {pickerOpen && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900/40">
          <p className="px-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Transfer to</p>
          {pickerLoading ? (
            <div className="flex items-center gap-1.5 px-1 py-2 text-xs text-gray-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : online.length === 0 ? (
            <p className="px-1 py-2 text-xs text-gray-500 dark:text-gray-400">No other agents are online.</p>
          ) : (
            <ul className="max-h-40 space-y-0.5 overflow-y-auto">
              {online.map((t) => (
                <li key={t.id}>
                  <button
                    onClick={() => { setPickerOpen(false); transferBlind(t.id); }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-gray-800 transition-colors duration-150 hover:bg-primary-50 dark:text-gray-200 dark:hover:bg-primary-900/20"
                  >
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${t.onCall ? 'bg-amber-500' : 'bg-green-500'}`}
                    />
                    <span className="truncate">{t.name}</span>
                    {t.onCall && (
                      <span className="ml-auto shrink-0 text-[10px] font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400">
                        On a call
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          onClick={() => setAudioOpen((v) => !v)}
          title="Audio devices (mic & speaker)"
          className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors duration-150 ${
            audioOpen
              ? 'border-primary-400 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
              : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
          }`}
        >
          <Headphones className="h-4 w-4" />
        </button>
        <button
          onClick={park}
          disabled={!canHandoff}
          title="Park (hold for any agent to pick up)"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <Pause className="h-4 w-4" />
        </button>
        <button
          onClick={() => setPickerOpen((v) => !v)}
          disabled={!canHandoff}
          title="Transfer to another agent"
          className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40 ${
            pickerOpen
              ? 'border-primary-400 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
              : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
          }`}
        >
          <PhoneForwarded className="h-4 w-4" />
        </button>
        <button
          onClick={toggleMute}
          disabled={!connected}
          title={muted ? 'Unmute' : 'Mute'}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>
        <button
          onClick={hangup}
          title="Hang up"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white transition-colors duration-150 hover:bg-red-700"
        >
          <PhoneOff className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
